import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import { Radar } from "lucide-react";
import { Arma, backendOrigin } from "@/lib/armaApi";
import { useInstance } from "@/context/InstanceContext";
import CornerFrame from "@/components/CornerFrame";
import HeroBanner from "@/components/HeroBanner";


const FACTION_COLOR = {
  US:   "#3b82f6",
  USSR: "#dc2626",
  FIA:  "#16a34a",
};
function factionColor(f) {
  return FACTION_COLOR[f] || "#6b7280";
}

function parsePos(posStr) {
  if (!posStr) return null;
  const parts = posStr.trim().split(/\s+/);
  if (parts.length < 3) return null;
  const x = parseFloat(parts[0]);
  const z = parseFloat(parts[2]);
  if (isNaN(x) || isNaN(z)) return null;
  return [z, x]; // Leaflet CRS.Simple [lat, lng] = [Z, X]
}

function gridFallbackUrl(size = 12800) {
  const lines = [];
  const step = 800;
  for (let i = 0; i <= size; i += step) {
    lines.push(`<line x1='${i}' y1='0' x2='${i}' y2='${size}' stroke='%232e3725' stroke-width='6'/>`);
    lines.push(`<line x1='0' y1='${i}' x2='${size}' y2='${i}' stroke='%232e3725' stroke-width='6'/>`);
  }
  return `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${size} ${size}'><rect width='100%25' height='100%25' fill='%2311140f'/>${lines.join("")}<rect x='0' y='0' width='${size}' height='${size}' fill='none' stroke='%23b08d57' stroke-width='10'/></svg>`;
}

export default function LiveMap() {
  const { instance, mapInfo } = useInstance();

  const [playerCount, setPlayerCount] = useState(0);
  const [vehicleCount, setVehicleCount] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState(null);
  const [mapReady, setMapReady] = useState(false);

  const mapRef = useRef(null);
  const mapEl = useRef(null);
  const overlayRef = useRef(null);

  // Marker registries: id → L.circleMarker
  const playerMarkers = useRef(new Map());
  const vehicleMarkers = useRef(new Map());
  const baseMarkers = useRef(new Map());

  const DEFAULT_BOUNDS = [[0, 0], [12800, 12800]];

  // Initialize Leaflet map
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const activeBounds = mapInfo?.bounds || DEFAULT_BOUNDS;
    const map = L.map(mapEl.current, {
      crs: L.CRS.Simple,
      minZoom: -3,
      maxZoom: 3,
      zoomSnap: 0.25,
      attributionControl: false,
      preferCanvas: true,
    });
    map.fitBounds(activeBounds);
    mapRef.current = map;
    setMapReady(true);
    return () => {
      map.remove();
      mapRef.current = null;
      setMapReady(false);
      playerMarkers.current.clear();
      vehicleMarkers.current.clear();
      baseMarkers.current.clear();
    };
  }, []);

  // Update map image when mapInfo changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (overlayRef.current) { overlayRef.current.remove(); overlayRef.current = null; }
    const activeBounds = mapInfo?.bounds || DEFAULT_BOUNDS;
    const size = mapInfo?.size || 12800;
    const url = mapInfo?.imageUrl ? `${backendOrigin()}${mapInfo.imageUrl}` : gridFallbackUrl(size);
    overlayRef.current = L.imageOverlay(url, activeBounds, { opacity: 1 }).addTo(map);
    map.fitBounds(activeBounds);
  }, [mapInfo]);

  // Polling
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current;
    if (!map) return;

    const fetchAndUpdate = async () => {
      try {
        const data = await Arma.liveMap(instance);
        setError(null);
        setLastUpdate(new Date());

        // --- Players ---
        const seenPlayers = new Set();
        for (const p of data.players) {
          const uid = p.m_sUID;
          if (!uid) continue;
          const pos = parsePos(p.m_sPosition);
          if (!pos) continue;
          seenPlayers.add(uid);
          const color = factionColor(p.m_sFaction);
          if (playerMarkers.current.has(uid)) {
            const m = playerMarkers.current.get(uid);
            m.setLatLng(pos);
            m.setStyle({ color, fillColor: color });
            m.setTooltipContent(`${p.m_sPlayerName} [${p.m_sFaction || "?"}]`);
          } else {
            const m = L.circleMarker(pos, {
              radius: 6,
              color,
              fillColor: color,
              fillOpacity: 0.9,
              weight: 1.5,
            })
              .bindTooltip(`${p.m_sPlayerName} [${p.m_sFaction || "?"}]`, { permanent: false })
              .addTo(map);
            playerMarkers.current.set(uid, m);
          }
        }
        for (const [uid, m] of playerMarkers.current) {
          if (!seenPlayers.has(uid)) { m.remove(); playerMarkers.current.delete(uid); }
        }
        setPlayerCount(seenPlayers.size);

        // --- Vehicles ---
        const seenVehicles = new Set();
        for (const v of data.vehicles) {
          // Aktualitaet filtert das Backend (Server-Uhr statt Browser-Uhr)
          const key = v.m_sEntityId;
          if (!key) continue;
          const pos = parsePos(v.m_sPosition);
          if (!pos) continue;
          seenVehicles.add(key);
          const color = factionColor(v.m_sFaction);
          const tooltip = `${v.m_sVehicleName || "Vehicle"} ×${v.m_iOccupantCount} [${v.m_sFaction || "?"}]`;
          if (vehicleMarkers.current.has(key)) {
            const m = vehicleMarkers.current.get(key);
            m.setLatLng(pos);
            m.setStyle({ color, fillColor: color });
            m.setTooltipContent(tooltip);
          } else {
            const m = L.circleMarker(pos, {
              radius: 9,
              color,
              fillColor: color,
              fillOpacity: 0.5,
              weight: 2,
              dashArray: "4 2",
            })
              .bindTooltip(tooltip, { permanent: false })
              .addTo(map);
            vehicleMarkers.current.set(key, m);
          }
        }
        for (const [key, m] of vehicleMarkers.current) {
          if (!seenVehicles.has(key)) { m.remove(); vehicleMarkers.current.delete(key); }
        }
        setVehicleCount(seenVehicles.size);

        // --- Bases ---
        const seenBases = new Set();
        for (const b of data.bases) {
          const name = b.baseName;
          if (!name) continue;
          const pos = parsePos(b.position);
          if (!pos) continue;
          seenBases.add(name);
          const color = factionColor(b.faction);
          const tooltip = `${name}\n${b.faction || "NEUTRAL"}`;
          if (baseMarkers.current.has(name)) {
            const m = baseMarkers.current.get(name);
            m.setLatLng(pos);
            m.setStyle({ color, fillColor: color });
            m.setTooltipContent(tooltip);
          } else {
            const m = L.circleMarker(pos, {
              radius: 12,
              color,
              fillColor: color,
              fillOpacity: 0.25,
              weight: 2,
            })
              .bindTooltip(tooltip, { permanent: false })
              .addTo(map);
            baseMarkers.current.set(name, m);
          }
        }
        for (const [name, m] of baseMarkers.current) {
          if (!seenBases.has(name)) { m.remove(); baseMarkers.current.delete(name); }
        }
      } catch (e) {
        setError(e?.message || "Fetch error");
      }
    };

    fetchAndUpdate();
    const id = setInterval(fetchAndUpdate, 5000);
    return () => clearInterval(id);
  }, [instance, mapReady]);

  const secAgo = lastUpdate ? Math.round((Date.now() - lastUpdate.getTime()) / 1000) : null;

  return (
    <div className="space-y-4 relative z-[2]">
      <HeroBanner
        eyebrow="LIVE TACTICAL OVERVIEW · ECHTZEIT"
        title="LIVE MAP"
        meta={
          <>
            AO: <span style={{ color: "var(--text)" }}>{mapInfo?.mapName || "—"}</span>
            <span className="mx-2">|</span>
            PLAYERS: <span style={{ color: "var(--amber-bright)" }}>{playerCount}</span>
            <span className="mx-2">|</span>
            VEHICLES: <span style={{ color: "var(--amber-bright)" }}>{vehicleCount}</span>
            {secAgo !== null && (
              <>
                <span className="mx-2">|</span>
                <span style={{ color: "var(--text-muted)" }}>vor {secAgo}s</span>
              </>
            )}
          </>
        }
      />

      {error && (
        <div className="tac-panel p-3 text-xs font-mono-tech" style={{ borderColor: "var(--red)", color: "var(--red-bright)" }}>
          {error}
        </div>
      )}

      <CornerFrame className="tac-panel">
        <div className="tac-panel-header">
          <div className="label-tech flex items-center gap-2" style={{ color: "var(--tan)" }}>
            <Radar className="w-3 h-3" />
            {mapInfo?.mapName || "MAP"} · CRS.SIMPLE · [Z,X]
          </div>
          <div className="label-tech" style={{ color: "var(--text-muted)" }}>LIVE · 5s POLL</div>
        </div>

        <div className="relative" style={{ height: "75vh", minHeight: 540, background: "#0f1410" }}>
          <div ref={mapEl} className="absolute inset-0" />

          {/* Compass */}
          <div className="absolute top-3 right-3 z-[450] pointer-events-none">
            <div className="px-2 py-1 text-[10px] font-mono-tech border" style={{
              background: "rgba(10,13,10,0.75)", borderColor: "var(--border-bright)", color: "var(--tan)",
            }}>▲ N</div>
          </div>

          {/* Legend */}
          <div className="absolute bottom-3 right-3 z-[450] pointer-events-none px-3 py-2 border space-y-1" style={{
            background: "rgba(10,13,10,0.8)", borderColor: "var(--border-bright)",
          }}>
            <div className="label-tech mb-1" style={{ color: "var(--tan)" }}>LEGENDE</div>
            {[
              { color: "#3b82f6", label: "US" },
              { color: "#dc2626", label: "USSR" },
              { color: "#16a34a", label: "FIA" },
            ].map(({ color, label }) => (
              <div key={label} className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full inline-block" style={{ background: color }} />
                <span className="text-[9px] font-mono-tech" style={{ color: "var(--text-muted)" }}>{label}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[9px] font-mono-tech" style={{ color: "var(--text-muted)" }}>● Spieler  ◎ Fahrzeug  ○ Basis</span>
            </div>
          </div>

          {!mapInfo?.imageUrl && (
            <div className="absolute bottom-3 left-3 z-[450] pointer-events-none px-3 py-1.5 border text-[10px] font-mono-tech" style={{
              background: "rgba(10,13,10,0.8)", borderColor: "var(--amber)", color: "var(--amber-bright)",
            }}>
              {`!! FALLBACK GRID – missing /maps/${mapInfo?.mapName || "?"}.png`}
            </div>
          )}
        </div>
      </CornerFrame>
    </div>
  );
}
